import React, { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  DndContext,
  DragEndEvent,
  closestCorners,
} from '@dnd-kit/core'
import { DragOverlay } from '@dnd-kit/core'
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { useAuth } from '../context/AuthContext'
import { Card } from '../components/common/Card'
import { AssignTaskModal } from '../components/common/AssignTaskModal'
import { Button } from '../components/common/Button'
import { LoadingSpinner } from '../components/common/LoadingSpinner'
import { TaskApprovalScreen } from '../components/features/TaskApprovalScreen'
import { RichTextContent } from '../components/common/RichTextContent'
import {
  KANBAN_GROUP_BY_LABELS,
  KANBAN_GROUP_BY_OPTIONS,
  TASK_PRIORITY_LABELS,
  TASK_STATUSES,
  TASK_STATUS_LABELS,
  TASK_TYPES,
  TASK_TYPE_LABELS,
} from '../lib/config/project-config'
import { isAssignableReviewRole, isSalesLikeRole } from '../lib/config/role-config'
import { canProfileSeeStatus, getVisibleStatusesForProfile } from '../lib/config/status-config'
import { mutations } from '../lib/supabase/mutations'
import { queries } from '../lib/supabase/queries'
import { RelatedAppRecord, Task } from '../lib/types/database.types'
import { sendTaskAssignedEmail, sendTaskReviewOutcomeEmail } from '../utils/emailNotifications'
import { formatDateTime } from '../utils/formatters'
import { getTaskCurrentStatusDueDate } from '../utils/statusDates'
import KanbanColumn from '../components/kanban/KanbanColumn'
import KanbanCard from '../components/kanban/KanbanCard'

type GroupBy = (typeof KANBAN_GROUP_BY_OPTIONS)[number]
const KANBAN_MY_TASKS_STORAGE_KEY = 'devtracker-kanban-my-tasks-only'
const DATE_BUCKET_ORDER = ['today', 'this_week', 'next_week', 'future', 'no_date'] as const
const normalizeResponsiblePerson = (value: string | null | undefined) => value?.trim().toLowerCase() || ''

const getDateBucket = (projectedCompletion: string | null): (typeof DATE_BUCKET_ORDER)[number] => {
  if (!projectedCompletion) {
    return 'no_date'
  }

  const [year, month, day] = projectedCompletion.split('-').map(Number)
  const dueDate = new Date(year, (month || 1) - 1, day || 1)
  const today = new Date()
  const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate())
  const dayOfWeek = startOfToday.getDay()
  const daysUntilMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1
  const startOfWeek = new Date(startOfToday)
  startOfWeek.setDate(startOfToday.getDate() - daysUntilMonday)

  const startOfNextWeek = new Date(startOfWeek)
  startOfNextWeek.setDate(startOfWeek.getDate() + 7)

  const startOfFollowingWeek = new Date(startOfNextWeek)
  startOfFollowingWeek.setDate(startOfNextWeek.getDate() + 7)

  if (dueDate.getTime() === startOfToday.getTime()) {
    return 'today'
  }

  if (dueDate >= startOfToday && dueDate < startOfNextWeek) {
    return 'this_week'
  }

  if (dueDate >= startOfNextWeek && dueDate < startOfFollowingWeek) {
    return 'next_week'
  }

  return 'future'
}

const getColumnKey = (task: Task, groupBy: GroupBy): string => {
  switch (groupBy) {
    case 'status':
      return task.status
    case 'assigned_to':
      return task.assigned_to || 'unassigned'
    case 'type':
      return task.type
    case 'related_app':
      return task.related_app_id || 'unknown_app'
    case 'date':
      return getDateBucket(getTaskCurrentStatusDueDate(task))
    default:
      return 'unknown'
  }
}

const getColumnLabel = (key: string, groupBy: GroupBy, profiles: Map<string, string>, relatedApps: Map<string, RelatedAppRecord>): string => {
  switch (groupBy) {
    case 'status':
      return TASK_STATUS_LABELS[key as keyof typeof TASK_STATUS_LABELS] || key
    case 'assigned_to':
      return key === 'unassigned' ? 'Unassigned' : profiles.get(key) || 'Unknown'
    case 'type':
      return TASK_TYPE_LABELS[key as keyof typeof TASK_TYPE_LABELS] || key
    case 'related_app':
      return key === 'unknown_app' ? 'Unknown App' : relatedApps.get(key)?.name || 'Unknown App'
    case 'date':
      if (key === 'today') return 'Today'
      if (key === 'this_week') return 'This Week'
      if (key === 'next_week') return 'Next Week'
      if (key === 'future') return 'Future'
      if (key === 'no_date') return 'No Date'
      return key
    default:
      return key
  }
}

const getOrderedColumnEntries = (grouped: Map<string, Task[]>, groupBy: GroupBy) => {
  if (groupBy === 'status') {
    return TASK_STATUSES.filter((status) => grouped.has(status)).map((status) => [status, grouped.get(status) || []] as const)
  }

  if (groupBy === 'type') {
    return TASK_TYPES.filter((type) => grouped.has(type)).map((type) => [type, grouped.get(type) || []] as const)
  }

  if (groupBy === 'related_app') {
    return Array.from(grouped.entries()).sort(([a], [b]) => a.localeCompare(b))
  }

  if (groupBy === 'date') {
    return DATE_BUCKET_ORDER.filter((bucket) => grouped.has(bucket)).map((bucket) => [bucket, grouped.get(bucket) || []] as const)
  }

  return Array.from(grouped.entries()).sort(([a], [b]) => a.localeCompare(b))
}

export const KanbanPage: React.FC = () => {
  const queryClient = useQueryClient()
  const { user, profile } = useAuth()
  const [groupBy, setGroupBy] = useState<GroupBy>('status')
  const [assigningTask, setAssigningTask] = useState<Task | null>(null)
  const [selectedAssignee, setSelectedAssignee] = useState('')
  const [assignError, setAssignError] = useState<string | null>(null)
  const [isViewOnly, setIsViewOnly] = useState(false)
  const [viewingTask, setViewingTask] = useState<Task | null>(null)
  const [reviewActionTaskId, setReviewActionTaskId] = useState<string | null>(null)
  const [includeCompleted, setIncludeCompleted] = useState(false)
  const [myTasksOnly, setMyTasksOnly] = useState<boolean>(() => {
    if (typeof window === 'undefined') {
      return true
    }

    const storedValue = window.localStorage.getItem(KANBAN_MY_TASKS_STORAGE_KEY)
    return storedValue ? storedValue === 'true' : true
  })

  const tasksQuery = useQuery({
    queryKey: ['tasks', 'kanban'],
    queryFn: () => queries.getTasks(false),
  })

  const profilesQuery = useQuery({
    queryKey: ['profiles', 'all'],
    queryFn: queries.getAllProfiles,
  })

  const relatedAppsQuery = useQuery({
    queryKey: ['related-apps'],
    queryFn: queries.getRelatedApps,
  })

  const updateTaskMutation = useMutation({
    mutationFn: ({ taskId, updates }: { taskId: string; updates: Parameters<typeof mutations.updateTask>[1] }) =>
      mutations.updateTask(taskId, updates),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['tasks'] })
    },
  })

  const taskPrerequisitesQuery = useQuery({
    queryKey: ['task-prerequisites'],
    queryFn: queries.getTaskPrerequisites,
  })

  const tasks = tasksQuery.data || []
  const profiles = profilesQuery.data || []
  const relatedApps = relatedAppsQuery.data || []
  const newRequestTaskList = useMemo(
    () => tasks.filter((task) => task.status === 'new'),
    [tasks]
  )

  React.useEffect(() => {
    if (typeof window === 'undefined') {
      return
    }

    window.localStorage.setItem(KANBAN_MY_TASKS_STORAGE_KEY, String(myTasksOnly))
  }, [myTasksOnly])

  const visibleStatuses = useMemo(
    () => getVisibleStatusesForProfile(profile),
    [profile]
  )

  // Filter tasks
  const filteredTasks = useMemo(() => {
    let result = [...tasks]

    result = result.filter((task) => canProfileSeeStatus(profile, task.status))

    if (!includeCompleted) {
      result = result.filter((task) => task.status !== 'completed')
    }
    
    // My Tasks filtering - role-dependent
    if (myTasksOnly && user) {
      if (isSalesLikeRole(profile?.role)) {
        const directlyMatchedTasks = result.filter((task) => task.created_by === user.id || task.assigned_to === user.id)
        const visibleParentIds = new Set(
          directlyMatchedTasks.filter((task) => !task.parent_task_id).map((task) => task.id)
        )

        result = result.filter(
          (task) =>
            task.created_by === user.id ||
            task.assigned_to === user.id ||
            (Boolean(task.parent_task_id) && visibleParentIds.has(task.parent_task_id!))
        )
      } else {
        // Dev & Admin users: My Tasks = tasks assigned to them
        result = result.filter((task) => task.assigned_to === user.id)
      }

      // My Tasks should never include unassigned records.
      result = result.filter((task) => Boolean(task.assigned_to))
    }
    return result
  }, [tasks, includeCompleted, myTasksOnly, user, profile])

  // Create profile lookup map
  const profileMap = useMemo(
    () => new Map(profiles.map((p) => [p.id, p.full_name || p.email || 'Unknown User'])),
    [profiles]
  )
  const relatedAppMap = useMemo(
    () => new Map(relatedApps.map((app) => [app.id, app])),
    [relatedApps]
  )
  const relatedAppResponsiblePersonMap = useMemo(() => {
    const normalizedProfiles = profiles.map((item) => ({
      id: item.id,
      fullName: normalizeResponsiblePerson(item.full_name),
      email: normalizeResponsiblePerson(item.email),
    }))

    return new Map(
      relatedApps.map((app) => {
        const responsiblePerson = normalizeResponsiblePerson(app.responsible_person)
        if (!responsiblePerson) {
          return [app.id, null]
        }

        const matchedProfile = normalizedProfiles.find(
          (item) => item.id === responsiblePerson || item.fullName === responsiblePerson || item.email === responsiblePerson
        )
        return [app.id, matchedProfile?.id || null]
      })
    )
  }, [profiles, relatedApps])
  const reviewAssigneeOptions = useMemo(
    () =>
      profiles
        .filter((item) => isAssignableReviewRole(item.role))
        .map((item) => ({ id: item.id, label: item.full_name || item.email || 'Unknown user' })),
    [profiles]
  )
  const prerequisiteByTaskId = useMemo(
    () => new Map((taskPrerequisitesQuery.data || []).map((item) => [item.task_id, item.prerequisite_task_id])),
    [taskPrerequisitesQuery.data]
  )
  const getTaskReadinessState = (task: Task): 'ready' | 'blocked' | null => {
    if (!task.parent_task_id) {
      return null
    }

    const prerequisiteTaskId = prerequisiteByTaskId.get(task.id)
    if (!prerequisiteTaskId) {
      return 'ready'
    }

    const prerequisiteTask = tasks.find((item) => item.id === prerequisiteTaskId)
    return prerequisiteTask?.status === 'released_to_dev' ? 'ready' : 'blocked'
  }

  // Group tasks by selected column
  const columns = useMemo(() => {
    const grouped = new Map<string, Task[]>()

    // Initialize all columns based on groupBy type
    if (groupBy === 'status') {
      visibleStatuses.forEach((status) => {
        grouped.set(status, [])
      })
    } else if (groupBy === 'type') {
      TASK_TYPES.forEach((type) => {
        grouped.set(type, [])
      })
    } else if (groupBy === 'date') {
      DATE_BUCKET_ORDER.forEach((bucket) => {
        grouped.set(bucket, [])
      })
    } else {
      // For assigned_to and related_app, we discover columns from tasks
      filteredTasks.forEach((task) => {
        const key = getColumnKey(task, groupBy)
        if (!grouped.has(key)) {
          grouped.set(key, [])
        }
      })
    }

    // Populate tasks into columns
    filteredTasks.forEach((task) => {
      const key = getColumnKey(task, groupBy)
      if (!grouped.has(key)) {
        grouped.set(key, [])
      }
      grouped.get(key)!.push(task)
    })

    // Sort tasks within each column by priority (high to low) then alphabetically
    const PRIORITY_ORDER = { high: 0, medium: 1, low: 2 }
    grouped.forEach((columnTasks) => {
      columnTasks.sort((a, b) => {
        const priorityDiff = (PRIORITY_ORDER[a.priority as keyof typeof PRIORITY_ORDER] ?? 999) - 
                            (PRIORITY_ORDER[b.priority as keyof typeof PRIORITY_ORDER] ?? 999)
        if (priorityDiff !== 0) return priorityDiff
        return a.title.localeCompare(b.title)
      })
    })

    return getOrderedColumnEntries(grouped, groupBy).slice(0, 10)
  }, [filteredTasks, groupBy, visibleStatuses])

  const isLoading = tasksQuery.isLoading || profilesQuery.isLoading || relatedAppsQuery.isLoading || taskPrerequisitesQuery.isLoading
  const error = tasksQuery.error || profilesQuery.error || relatedAppsQuery.error || taskPrerequisitesQuery.error

  const openAssignModal = (task: Task) => {
    if (isSalesLikeRole(profile?.role)) {
      return
    }

    setAssigningTask(task)
    setSelectedAssignee(task.assigned_to || '')
    setAssignError(null)
  }

  const closeAssignModal = () => {
    if (updateTaskMutation.isPending) {
      return
    }

    setAssigningTask(null)
    setSelectedAssignee('')
    setAssignError(null)
  }

  const handleAssignTask = async () => {
    if (!assigningTask || !selectedAssignee) {
      setAssignError('Please select a user to assign this task.')
      return
    }

    setAssignError(null)

    try {
      await updateTaskMutation.mutateAsync({
        taskId: assigningTask.id,
        updates: { assigned_to: selectedAssignee },
      })

      // Send email notification to the assigned user
      const assignedProfile = profiles.find((p) => p.id === selectedAssignee)
      if (assignedProfile?.email) {
        await sendTaskAssignedEmail(assignedProfile.email, assigningTask)
      }

      setAssigningTask(null)
      setSelectedAssignee('')
    } catch (mutationError) {
      setAssignError(mutationError instanceof Error ? mutationError.message : 'Unable to assign task.')
    }
  }

  const handleDragEnd = async (event: DragEndEvent) => {
    // Sales users cannot modify tasks
    if (isSalesLikeRole(profile?.role)) {
      return
    }

    const { active, over } = event

    if (!over) {
      return
    }

    const taskId = active.id as string
    const task = tasks.find((t) => t.id === taskId)
    if (!task) {
      return
    }

    const fromColumnKey = getColumnKey(task, groupBy)
    const toColumnKey = over.id as string

    if (fromColumnKey === toColumnKey) {
      return
    }

    // Build the update payload based on groupBy type
    const updates: Record<string, unknown> = {}

    switch (groupBy) {
      case 'status':
        updates.status = toColumnKey
        break
      case 'assigned_to':
        updates.assigned_to = toColumnKey === 'unassigned' ? null : toColumnKey
        break
      case 'type':
        updates.type = toColumnKey
        break
      case 'related_app':
        updates.related_app_id = toColumnKey === 'unknown_app' ? null : toColumnKey
        break
    }

    await updateTaskMutation.mutateAsync({
      taskId,
      updates: updates as Parameters<typeof mutations.updateTask>[1],
    })
  }

  const handleReviewStatusChange = async (taskId: string, status: 'approved' | 'rejected', assigneeId: string | null = null) => {
    if (status === 'approved' && !assigneeId) {
      return
    }

    setReviewActionTaskId(taskId)

    try {
      const targetTask = tasks.find((task) => task.id === taskId)
      const updates: Parameters<typeof mutations.updateTask>[1] = { status }
      if (status === 'approved') {
        updates.assigned_to = assigneeId
      }

      await updateTaskMutation.mutateAsync({
        taskId,
        updates,
      })

      if (targetTask) {
        const updatedTask: Task = {
          ...targetTask,
          status,
          assigned_to: status === 'approved' ? assigneeId : targetTask.assigned_to,
        }

        const notifications: Array<Promise<boolean>> = []
        const creatorProfile = profiles.find((item) => item.id === targetTask.created_by)
        const creatorEmail = creatorProfile?.email
        const reviewerName = profile?.full_name || profile?.email || 'Task approver'
        if (creatorEmail) {
          notifications.push(sendTaskReviewOutcomeEmail(creatorEmail, updatedTask, status, reviewerName))
        }

        if (status === 'approved' && assigneeId) {
          const assignedProfile = profiles.find((item) => item.id === assigneeId)
          if (assignedProfile?.email) {
            notifications.push(sendTaskAssignedEmail(assignedProfile.email, updatedTask))
          }
        }

        if (notifications.length > 0) {
          await Promise.allSettled(notifications)
        }
      }
    } finally {
      setReviewActionTaskId(null)
    }
  }

  return (
    <div className="space-y-6">
      <TaskApprovalScreen
        isTaskApprover={Boolean(profile?.is_task_approver)}
        tasks={newRequestTaskList}
        relatedAppResponsiblePersonMap={relatedAppResponsiblePersonMap}
        profileMap={profileMap}
        assigneeOptions={reviewAssigneeOptions}
        activeTaskId={reviewActionTaskId}
        onApprove={(task, assigneeId) => handleReviewStatusChange(task.id, 'approved', assigneeId)}
        onReject={(task) => handleReviewStatusChange(task.id, 'rejected')}
      />

      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Kanban Board</h1>
          <p className="text-sm text-gray-600">Organize and move tasks across your workflow.</p>
        </div>
      </div>

      {/* Controls */}
      <div className="flex flex-wrap gap-4 rounded-lg border border-stone-300 bg-stone-100 p-4">
        <div className="flex items-center gap-3">
          <span className="text-sm font-medium text-gray-700">Group by:</span>
          <div className="flex gap-2">
            {KANBAN_GROUP_BY_OPTIONS.map((option) => (
              <Button
                key={option}
                variant={groupBy === option ? 'primary' : 'secondary'}
                size="sm"
                onClick={() => setGroupBy(option)}
              >
                {KANBAN_GROUP_BY_LABELS[option]}
              </Button>
            ))}
          </div>
        </div>

        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={includeCompleted}
              onChange={(event) => setIncludeCompleted(event.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="font-medium">Include Completed</span>
          </label>

          <div className="h-8 w-0.5 bg-stone-400"></div>

          <Button
            variant={myTasksOnly ? 'primary' : 'secondary'}
            size="sm"
            onClick={() => setMyTasksOnly(!myTasksOnly)}
          >
            {isSalesLikeRole(profile?.role)
              ? myTasksOnly
                ? '✓ My Created Tasks'
                : 'Show All Tasks'
              : myTasksOnly
                ? '✓ My Tasks Only'
                : 'All Tasks'}
          </Button>
        </div>
      </div>

      {/* Board */}
      {isLoading ? (
        <Card>
          <div className="flex min-h-[400px] items-center justify-center">
            <LoadingSpinner text="Loading tasks..." />
          </div>
        </Card>
      ) : error ? (
        <Card>
          <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
            {error instanceof Error ? error.message : 'Failed to load tasks.'}
          </div>
        </Card>
      ) : columns.length === 0 ? (
        <Card>
          <div className="flex min-h-[400px] flex-col items-center justify-center gap-4 text-center">
            <p className="text-base font-medium text-gray-900">No tasks available</p>
            <p className="text-sm text-gray-500">Create tasks to start organizing your workflow.</p>
          </div>
        </Card>
      ) : (
        <DndContext onDragEnd={handleDragEnd} collisionDetection={closestCorners}>
          <div className="space-y-4 overflow-x-auto pb-4">
            <div className="flex gap-4 min-w-max">
              {columns.map(([columnKey, columnTasks]) => {
                const columnLabel = getColumnLabel(columnKey, groupBy, profileMap, relatedAppMap)

                return (
                  <KanbanColumn
                    key={columnKey}
                    id={columnKey}
                    title={columnLabel}
                    taskCount={columnTasks.length}
                  >
                    <SortableContext items={columnTasks.map((t) => t.id)} strategy={verticalListSortingStrategy}>
                      <div className="space-y-3">
                        {columnTasks.map((task) => (
                          <KanbanCard
                            key={task.id}
                            task={task}
                            profileMap={profileMap}
                            readinessState={getTaskReadinessState(task)}
                            canAssign={true}
                            onAssign={openAssignModal}
                            onView={(task) => {
                              setIsViewOnly(true)
                              setViewingTask(task)
                            }}
                          />
                        ))}
                      </div>
                    </SortableContext>
                  </KanbanColumn>
                )
              })}
            </div>
          </div>

          <DragOverlay>
            {/* Show a preview of the dragged card */}
          </DragOverlay>
        </DndContext>
      )}

      <AssignTaskModal
        task={assigningTask}
        profiles={profiles}
        selectedAssignee={selectedAssignee}
        onSelectedAssigneeChange={setSelectedAssignee}
        onClose={closeAssignModal}
        onAssign={handleAssignTask}
        isLoading={updateTaskMutation.isPending}
        error={assignError}
      />
      {viewingTask && isViewOnly && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center bg-black bg-opacity-50 sm:items-center"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setIsViewOnly(false)
              setViewingTask(null)
            }
          }}
        >
          <div className="w-full max-w-2xl rounded-t-lg bg-white sm:rounded-lg">
            <div className="space-y-6 p-6">
              <div className="flex items-start justify-between border-b border-gray-200 pb-4">
                <h2 className="text-xl font-semibold text-gray-900">{viewingTask.title}</h2>
                <button
                  onClick={() => {
                    setIsViewOnly(false)
                    setViewingTask(null)
                  }}
                  className="text-gray-500 hover:text-gray-700"
                >
                  ✕
                </button>
              </div>

              <div className="space-y-4">
                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <p className="text-xs uppercase tracking-wide text-gray-500">Status</p>
                    <p className="text-sm font-medium text-gray-900">
                      {TASK_STATUS_LABELS[viewingTask.status as keyof typeof TASK_STATUS_LABELS] || viewingTask.status}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wide text-gray-500">Priority</p>
                    <p className="text-sm font-medium text-gray-900">
                      {TASK_PRIORITY_LABELS[viewingTask.priority as keyof typeof TASK_PRIORITY_LABELS] || viewingTask.priority}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wide text-gray-500">Type</p>
                    <p className="text-sm font-medium text-gray-900">
                      {TASK_TYPE_LABELS[viewingTask.type as keyof typeof TASK_TYPE_LABELS] || viewingTask.type}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wide text-gray-500">Related App</p>
                    <p className="text-sm font-medium text-gray-900">{viewingTask.related_app || '-'}</p>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wide text-gray-500">Assigned To</p>
                    <p className="text-sm font-medium text-gray-900">
                      {viewingTask.assigned_to ? profileMap.get(viewingTask.assigned_to) || 'Unknown' : 'Unassigned'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wide text-gray-500">Created</p>
                    <p className="text-sm font-medium text-gray-900">{formatDateTime(viewingTask.created_at)}</p>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wide text-gray-500">Due (Current Status)</p>
                    <p className="text-sm font-medium text-gray-900">{getTaskCurrentStatusDueDate(viewingTask) || 'Not set'}</p>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wide text-gray-500">Support Ticket No</p>
                    <p className="text-sm font-medium text-gray-900">{viewingTask.support_ticket_no || 'Not set'}</p>
                  </div>
                </div>

                <div>
                  <p className="text-xs uppercase tracking-wide text-gray-500">Description</p>
                  <div className="mt-2 rounded-lg bg-gray-50 p-3 text-sm text-gray-700">
                    {viewingTask.description ? <RichTextContent html={viewingTask.description} /> : 'No description provided.'}
                  </div>
                </div>
              </div>

              <div className="flex justify-end">
                <Button
                  variant="ghost"
                  onClick={() => {
                    setIsViewOnly(false)
                    setViewingTask(null)
                  }}
                >
                  Close
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default KanbanPage
